import { type BrandName } from "@elit-hair/brands";

export type SalesCallResultOption = {
    value: string;
    labelKey: string;
    /** Tailwind background class for the status color indicator (e.g. `bg-emerald-500`). */
    colorClass: string;
    /** Trigger tint so the selected state color stays visible when the select is closed. */
    tintClass: string;
};

const RHINOMEDICA_SALES_CALL_RESULTS: ReadonlyArray<SalesCallResultOption> = [
    {
        value: "not_reached",
        labelKey: "sales_call_result_not_reached",
        colorClass: "bg-slate-500",
        tintClass: "border-slate-500/40 bg-slate-500/10",
    },
    {
        value: "offer_sent",
        labelKey: "sales_call_result_offer_sent",
        colorClass: "bg-sky-500",
        tintClass: "border-sky-500/40 bg-sky-500/10",
    },
    {
        value: "suggested_operation_date",
        labelKey: "sales_call_result_suggested_operation_date",
        colorClass: "bg-emerald-500",
        tintClass: "border-emerald-500/40 bg-emerald-500/10",
    },
    {
        value: "no_interest",
        labelKey: "sales_call_result_no_interest",
        colorClass: "bg-red-500",
        tintClass: "border-red-500/40 bg-red-500/10",
    },
    {
        value: "contact_later",
        labelKey: "sales_call_result_contact_later",
        colorClass: "bg-amber-500",
        tintClass: "border-amber-500/40 bg-amber-500/10",
    },
    {
        value: "not_suitable",
        labelKey: "sales_call_result_not_suitable",
        colorClass: "bg-rose-500",
        tintClass: "border-rose-500/40 bg-rose-500/10",
    },
    {
        value: "rescheduled",
        labelKey: "sales_call_result_rescheduled",
        colorClass: "bg-violet-500",
        tintClass: "border-violet-500/40 bg-violet-500/10",
    },
];

/** Brand-specific appointment resolution options. Brands without an entry have no selectable statuses yet. */
export const SALES_CALL_RESULTS_BY_BRAND: Partial<Record<BrandName, ReadonlyArray<SalesCallResultOption>>> = {
    rhinomedica: RHINOMEDICA_SALES_CALL_RESULTS,
};

/** Sales call results that count as a conversion for analytics. */
export const CONVERTED_SALES_CALL_RESULTS = new Set(["offer_sent", "suggested_operation_date"]);

export const PENDING_SALES_CALL_RESULT = "pending";

export const UNSELECTED_SALES_CALL_RESULT_OPTION: SalesCallResultOption = {
    value: PENDING_SALES_CALL_RESULT,
    labelKey: "sales_call_result_not_selected",
    colorClass: "bg-muted-foreground/50",
    tintClass: "border-border bg-muted/40",
};

export const getSalesCallResultOptions = (brandName?: BrandName): ReadonlyArray<SalesCallResultOption> => {
    if (!brandName) {
        return [];
    }

    return SALES_CALL_RESULTS_BY_BRAND[brandName] ?? [];
};

export const getSalesCallResultOption = (
    brandName: BrandName | undefined,
    value: string | null | undefined
): SalesCallResultOption | undefined => {
    if (!value) {
        return undefined;
    }

    return getSalesCallResultOptions(brandName).find((option) => option.value === value);
};
